import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';

import { LinkClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import meetingNotePoster from 'src/assets/img/rm-meeting-note-poster.webp';
import meetingNoteVideoMp4 from 'src/assets/img/rm-meeting-note.mp4';
import meetingNoteVideo from 'src/assets/img/rm-meeting-note.webm';
import { Button } from 'src/components/Button';
import { NewsCard } from 'src/components/NewsCard';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

interface MethodsCardProps {
  hasSubscription: boolean;
}

export const MeetingNotesCard = ({ hasSubscription }: MethodsCardProps) => {
  const { t } = useLingui();

  return (
    <NewsCard
      // mp4 fallback: Older Safari doesn't support webm, so it needs an h264 source to autoplay.
      videoSources={[
        { type: 'webm', url: meetingNoteVideo },
        { type: 'mp4', url: meetingNoteVideoMp4 },
      ]}
      poster={meetingNotePoster}
      mediaAlt="Meeting note UI on the reMarkable paper tablet"
      mediaLink="/integrations"
      headline={t`Meeting notes tool`}
      mediaOnClick={() => {
        tracker.trackEvent(
          new LinkClicked({
            component_location:
              ComponentLocations.FRONT_PAGE.MEETING_NOTES_CARD,
            text: t`Meeting notes tool`,
            link_source: '/integrations',
          })
        );
      }}
    >
      <div className="flex flex-col gap-32">
        <div className="flex flex-col gap-16">
          <Typography className="m-0 text-left" variant="responsive-heading-md">
            <Trans>A head start for your meetings</Trans>
          </Typography>
          {hasSubscription ? (
            <Tag variant="success-light" className="whitespace-nowrap">
              <Trans>New</Trans>
            </Tag>
          ) : (
            <Tag variant="info-light" className="whitespace-nowrap">
              <Trans>Connect Feature</Trans>
            </Tag>
          )}
          <Typography
            className="m-0 text-left"
            textWrap="pretty"
            variant="body-md"
          >
            <Trans>
              Get meeting notes with key details from your Outlook or Google
              calendar, so you can stay present in the conversation and capture
              ideas to revisit later.
            </Trans>
          </Typography>
        </div>
        <div className="flex flex-row flex-wrap gap-32">
          <Button
            as="a"
            variant="secondary"
            to="/integrations"
            analytics={{
              location: ComponentLocations.FRONT_PAGE.MEETING_NOTES_CARD,
              action: 'go to integrations',
            }}
          >
            <span>
              <Trans>Add your calendar</Trans>
            </span>
            <ArrowRight />
          </Button>
          <Button
            as="a"
            variant="tertiary-neutral"
            to={SUPPORT_URL.ARTICLES.HOW_TO_USE_MEETING_NOTES}
            analytics={{
              location: ComponentLocations.FRONT_PAGE.MEETING_NOTES_CARD,
              action: 'learn more',
            }}
          >
            <Trans>Learn more</Trans>
          </Button>
        </div>
      </div>
    </NewsCard>
  );
};
